import React, { type FC, useCallback, useContext, useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardBody,
  Circle,
  Collapse,
  Flex,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  type MenuProps,
  Stack,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  type UseFieldArrayReturn,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from '@/components';
import { type CheckBoxFieldOption } from '@/components/Form/FieldCheckboxes';
import {
  IconAi,
  IconCheck,
  IconDocument,
  IconDone,
  IconInfoCircle,
  IconList,
  IconPlayCircle,
  IconPlus,
  IconRightArrow,
  IconXClose,
} from '@/components/Icons/icons-generated/';
import IconArrowDown from '@/components/Icons/icons-generated/IconArrowDown';
import { AVAILABLE_LANGUAGES } from '@/lib/i18n/constants';
import {
  type AdminQuestionChoiceDto,
  type AiQuestionModel,
  InterviewType,
  QuestionGenerationType,
  QuestionType,
} from '@/services/api';

import { useGetAccountQuery } from '../auth/useAuthQueries';
import { AdminNewInterviewContext } from './PageAdminNewInterview.ctx';
import type { NewInterviewFormInputs } from './common';
import {
  useCreateAndGetInterviewAiQuestionsMutation,
  useCreateInterviewSkillMutation,
} from './useInterviewQueries';

const MINIMUM_QUESTIONS_TO_SHOW = 3;

type AiQuestionsForm = {
  positionName: string;
  jobDescription: string;
};
type AiQuestionsFormCardProps = {
  formArray: UseFieldArrayReturn<NewInterviewFormInputs, 'questions', 'id'>;
  positionName?: string;
};

export const AiQuestionsGenerate: React.FC<AiQuestionsFormCardProps> = ({
  formArray,
  positionName,
}) => {
  const { watch } = useFormContext<NewInterviewFormInputs>();
  const { append, fields, remove, update } = formArray;
  const { t } = useTranslation(['interview', 'common']);
  const { jobDescription } = useContext(AdminNewInterviewContext);
  const [showLess, setShowLess] = useState(false);
  const [allAiQuestions, setAllAiQuestions] = useState<Array<AiQuestionModel>>(
    []
  );
  const [aiQuestions, setAiQuestions] = useState<Array<AiQuestionModel>>([]);

  const aiForm = useForm<AiQuestionsForm>({
    defaultValues: {
      positionName,
      jobDescription: '',
    },
  });

  const toast = useToast();

  const createInterviewAiQuestionsMutation =
    useCreateAndGetInterviewAiQuestionsMutation({
      onError() {
        toast({
          title: t('interview:createNewInterview.aiQuestionsError'),
          status: 'error',
        });
      },
    });

  const createInterviewSkillMutation = useCreateInterviewSkillMutation();

  const handleAddQuestion = useCallback(
    async (checked: boolean, question: AiQuestionModel) => {
      if (checked) {
        if (fields.length === 1) {
          // Clear the first question if list is empty
          return update(0, {
            question: '',
            durationMinutes: undefined,
            isCaseMode: false,
            skill: undefined,
          });
        }
        return remove(
          fields.findIndex((field) => field.question === question.question)
        );
      }

      // Clear the first question if list is empty
      const firstQuestion = watch('questions.0.question');
      if (fields.length === 1 && !firstQuestion) remove(0);

      const choices: Array<CheckBoxFieldOption> =
        question.choices?.map((q) => ({
          label: q.title ?? '',
          checked:
            question.questionType === QuestionType.MultipleSelection
              ? false
              : (q.isCorrect ?? false),
          value: q.title,
        })) ?? [];

      // Then append the question
      append(
        {
          question: question.question,
          questionMarkdown: question.question,
          durationMinutes: question.time,
          skill: question.skill,
          questionType: question.questionType ?? QuestionType.Video,
          choices: choices as Array<AdminQuestionChoiceDto>,
          tags: question.tags,
          isCaseMode: question.questionGenerationType === 'Case',
          isAiGenerated: true,
        },
        { shouldFocus: false }
      );

      // add question's ai-generated skill for this interview
      await createInterviewSkillMutation.mutateAsync({ skill: question.skill });
    },
    [append, fields, remove, update, watch, createInterviewSkillMutation]
  );

  const questionsHasBeenFetchedBefore = Boolean(allAiQuestions.length);

  const handleQuestionGeneration = async (
    data: AiQuestionsForm,
    questionType: QuestionGenerationType
  ) => {
    const result = await createInterviewAiQuestionsMutation.mutateAsync({
      jobDescription: data.jobDescription,
      questionGenerationType: questionType,
      title: data.positionName,
      olderQuestions:
        allAiQuestions.length === 0
          ? undefined
          : allAiQuestions.map((item) => item.question!),
      type: watch('type'),
      language:
        watch('type') === InterviewType.LanguageTest
          ? watch('language')
          : undefined,
    });

    setAllAiQuestions((prev) => [...prev, ...result]);

    setAiQuestions((prev) => [...prev, ...result]);

    if (jobDescription) {
      jobDescription.current = data.jobDescription;
    }
  };

  return (
    <Card p={5} gap={4}>
      <AiInfoMessage />

      <CardBody
        p={4}
        rounded="xl"
        background="linear-gradient(96.88deg, rgba(191, 140, 255, 0.12) 20.47%, rgba(255, 46, 0, 0.0384) 146.54%);"
      >
        <VStack>
          <Form noHtmlForm {...aiForm}>
            <FormField
              type="text"
              rules={{
                required: t(
                  'interview:createNewInterview.positionNameRequired'
                ),
              }}
              label={t('interview:createNewInterview.positionNameLabel')}
              name="positionName"
              background="white"
              optionalityHint="required"
              control={aiForm.control}
            />
            <FormField
              type="markdown"
              label={t('interview:createNewInterview.jobDescriptionLabel')}
              name="jobDescription"
              rules={{
                required: t(
                  'interview:createNewInterview.jobDescriptionRequired'
                ),
              }}
              placeholder={t('interview:createNewInterview.enterJobDetails')}
              optionalityHint="required"
              control={aiForm.control}
            />
            <HStack alignSelf="flex-start" spacing={1} color="gray.500">
              <IconInfoCircle />
              <Text fontSize="sm">
                {t('interview:jobDescription.helperText')}
              </Text>
            </HStack>

            <Menu>
              {({ isOpen }) => (
                <>
                  <MenuButton
                    variant="@primary"
                    as={Button}
                    isDisabled={!aiForm.formState.isDirty}
                    isLoading={createInterviewAiQuestionsMutation.isPending}
                    leftIcon={<IconAi />}
                    alignSelf="flex-end"
                    size="sm"
                    rightIcon={
                      <Stack>
                        <IconArrowDown
                          color="brand.100"
                          transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                          transition="transform 0.2s ease-in-out"
                        />
                      </Stack>
                    }
                  >
                    {questionsHasBeenFetchedBefore
                      ? t('interview:createNewInterview.createMoreQuestions')
                      : t('interview:createNewInterview.createQuestions')}
                  </MenuButton>
                  <MenuList padding={0} borderRadius="12px">
                    <MenuItem
                      leftIcon={
                        <IconPlayCircle color="gray.500" fontSize={20} />
                      }
                      borderBottomRadius={0}
                      as={Button}
                      display="flex"
                      variant="unstyled"
                      justifyContent="start"
                      type="submit"
                      onClick={aiForm.handleSubmit(
                        async (data) =>
                          await handleQuestionGeneration(
                            data,
                            QuestionGenerationType.Video
                          )
                      )}
                    >
                      {t('interview:createNewInterview.createVideoQuestion')}
                    </MenuItem>
                    <MenuItem
                      borderRadius={0}
                      as={Button}
                      type="submit"
                      variant="unstyled"
                      leftIcon={<IconList color="gray.500" fontSize={20} />}
                      display="flex"
                      justifyContent="start"
                      onClick={aiForm.handleSubmit(
                        async (data) =>
                          await handleQuestionGeneration(
                            data,
                            QuestionGenerationType.MultipleChoice
                          )
                      )}
                    >
                      {t(
                        'interview:createNewInterview.questionPreparation.questionTypeOptions.MultipleChoice'
                      )}
                    </MenuItem>
                    <MenuItem
                      borderRadius={0}
                      as={Button}
                      type="submit"
                      variant="unstyled"
                      leftIcon={<IconList color="gray.500" fontSize={20} />}
                      display="flex"
                      justifyContent="start"
                      onClick={aiForm.handleSubmit(
                        async (data) =>
                          await handleQuestionGeneration(
                            data,
                            QuestionGenerationType.MultipleSelect
                          )
                      )}
                    >
                      {t(
                        'interview:createNewInterview.questionPreparation.questionTypeOptions.MultipleSelection'
                      )}
                    </MenuItem>
                    <MenuItem
                      borderRadius={0}
                      as={Button}
                      type="submit"
                      variant="unstyled"
                      leftIcon={<IconList color="gray.500" fontSize={20} />}
                      display="flex"
                      justifyContent="start"
                      onClick={aiForm.handleSubmit(
                        async (data) =>
                          await handleQuestionGeneration(
                            data,
                            QuestionGenerationType.OpenEnded
                          )
                      )}
                    >
                      {t(
                        'interview:createNewInterview.questionPreparation.questionTypeOptions.OpenEnded'
                      )}
                    </MenuItem>
                    <MenuItem
                      borderTopRadius={0}
                      as={Button}
                      type="submit"
                      variant="unstyled"
                      leftIcon={<IconDocument color="gray.500" fontSize={20} />}
                      display="flex"
                      justifyContent="start"
                      onClick={aiForm.handleSubmit(
                        async (data) =>
                          await handleQuestionGeneration(
                            data,
                            QuestionGenerationType.Case
                          )
                      )}
                    >
                      {t('interview:createNewInterview.createCaseQuestion')}
                    </MenuItem>
                  </MenuList>
                </>
              )}
            </Menu>
          </Form>
        </VStack>
      </CardBody>

      <CardBody p={0} display={aiQuestions.length === 0 ? 'none' : undefined}>
        <FormLabel fontSize="sm">
          {t('interview:createNewInterview.aiQuestionsLabel')}
        </FormLabel>

        <Flex
          pb={3}
          flexDirection="column"
          border="1px solid"
          borderColor="gray.50"
          px={0}
          borderRadius={12}
        >
          {aiQuestions?.map((question, index) => {
            const checked = fields.some(
              (field) => field.question === question.question
            );

            const isLast = index === aiQuestions.length - 1;

            return (
              <Collapse
                in={!showLess || index < MINIMUM_QUESTIONS_TO_SHOW}
                key={question.question}
                animateOpacity
              >
                <Box px={3}>
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    columnGap={4}
                    py={3}
                    borderBottom={isLast ? undefined : '1px solid'}
                    borderBottomColor="gray.50"
                  >
                    <IconButton
                      aria-label={t('interview:createNewInterview.addQuestion')}
                      shadow="none"
                      borderColor={checked ? 'brand.500' : 'gray.50'}
                      color="brand.500"
                      aspectRatio={1}
                      borderRadius={6}
                      minWidth="32px"
                      height="32px"
                      icon={
                        checked ? (
                          <IconCheck fontSize="xl" />
                        ) : (
                          <IconPlus fontSize="sm" />
                        )
                      }
                      onClick={() => handleAddQuestion(checked, question)}
                      flexShrink={0}
                    />
                    <Stack flex={1}>
                      <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                        width="full"
                      >
                        <Flex gap={2} alignItems="center">
                          <Text>{question.question}</Text>
                        </Flex>
                        <Flex
                          alignItems="center"
                          columnGap="1"
                          px={2}
                          bg="primary.50"
                          borderRadius="full"
                        >
                          <IconDone color="primary.700" />
                          <Text
                            whiteSpace="nowrap"
                            fontSize="12px"
                            fontWeight="semibold"
                            color="primary.700"
                          >
                            {question.skill}
                          </Text>
                        </Flex>
                      </Flex>

                      {question.choices?.map((choice) => (
                        <Flex
                          key={choice.title}
                          alignItems="center"
                          columnGap={2}
                          fontSize="sm"
                          color="gray.500"
                        >
                          &#x2022;
                          <Text>
                            {choice.title}
                            {choice.isCorrect && (
                              <Text as="span" fontWeight="500" color="gray.900">
                                {' '}
                                (True)
                              </Text>
                            )}
                          </Text>
                        </Flex>
                      )) ?? null}
                    </Stack>

                    <IconXClose
                      fontSize="16px"
                      cursor="pointer"
                      onClick={() => {
                        const checked = fields.some(
                          (field) => field.question === question.question
                        );

                        if (checked) {
                          // delete the question from selected questions.
                          handleAddQuestion(checked, question);
                        }

                        setAiQuestions((prev) =>
                          prev.filter((q) => q.question !== question.question)
                        );
                      }}
                    />
                  </Flex>
                </Box>
              </Collapse>
            );
          })}

          <Button
            w="110px"
            variant="link"
            color="brand.500"
            size="sm"
            columnGap={2}
            ml="auto"
            mr={3}
            mt={3}
            onClick={() => setShowLess((prev) => !prev)}
          >
            <IconRightArrow
              fontSize="lg"
              color="brand.500"
              transform={showLess ? 'rotate(90deg)' : 'rotate(-90deg)'}
            />
            {showLess
              ? t('common:actions.showMore')
              : t('common:actions.showLess')}
          </Button>
        </Flex>
      </CardBody>
    </Card>
  );
};

const AiInfoMessage: FC = () => {
  const { t } = useTranslation(['interview']);
  const account = useGetAccountQuery();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

  if (account.data?.aiQuestionCreatedBefore) return null;

  return (
    <Collapse in={isOpen} animateOpacity>
      <Flex
        p={3}
        bg="info.25"
        borderColor="info.50"
        border={1}
        borderRadius={12}
        justifyContent="space-between"
        alignItems="start"
        columnGap={4}
      >
        <Flex alignItems="center" columnGap={3}>
          <Circle size="36px" bg="info.50">
            <IconInfoCircle fontSize="24px" color="info.500" />
          </Circle>

          <Text color="gray.900" fontWeight="medium">
            {t('interview:aiInfoMessage')}
          </Text>
        </Flex>

        <IconXClose
          fontSize="20px"
          cursor="pointer"
          onClick={() => {
            onToggle();
          }}
        />
      </Flex>
    </Collapse>
  );
};

export const LanguageSelectionMenu = ({
  isDisabled,
  renderAsBottomSheet,
  ...rest
}: Omit<MenuProps, 'children'> & {
  isDisabled?: boolean;
  renderAsBottomSheet?: boolean;
}) => {
  const { setValue, watch } = useFormContext<NewInterviewFormInputs>();
  const { i18n } = useTranslation();
  const bottomSheetDisclosure = useDisclosure();

  const displayLanguage = AVAILABLE_LANGUAGES.find((l) =>
    watch('language') ? watch('language') === l.title : l.key === i18n.language
  );
  const Flag = displayLanguage?.flag;

  return (
    <Box ml="auto">
      <Menu placement="bottom-end" {...rest}>
        {({ isOpen }) => {
          return (
            <>
              <MenuButton
                isDisabled={isDisabled}
                color="brand.900"
                padding="0"
                borderRadius="8"
                as={Button}
                variant="outline"
                size="md"
                border="1px solid"
                borderColor="gray.100"
                py={2}
                px={3}
                bg="white"
                onClick={() => {
                  if (renderAsBottomSheet) {
                    bottomSheetDisclosure.onToggle();
                  }
                }}
              >
                <HStack gap={2}>
                  {Flag && <Flag w="32px" h="32px" fontSize="24px" />}
                  <IconArrowDown
                    color="gray.300"
                    transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                    transition="transform 0.2s ease-in-out"
                  />
                </HStack>
              </MenuButton>

              <MenuList minW="6rem" overflow="hidden">
                {AVAILABLE_LANGUAGES.map((lang) => (
                  <MenuItem
                    key={lang.key}
                    onClick={() => setValue('language', lang.title)}
                  >
                    <Icon
                      as={lang.flag}
                      w="32px"
                      h="32px"
                      mr={1}
                      fontSize="24px"
                    />
                    {lang.key.toLocaleUpperCase()}
                  </MenuItem>
                ))}
              </MenuList>
            </>
          );
        }}
      </Menu>
    </Box>
  );
};
